import { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { flush, pendingFor } from "@/lib/requests/queue";
import { submitRequest } from "@/lib/requests/submit";

export function useRequestQueue() {
  const { user } = useAuth();
  const ownerId = user?.id ?? null;
  const [pending, setPending] = useState(0);

  const refresh = useCallback(() => {
    setPending(pendingFor(ownerId));
  }, [ownerId]);

  const runFlush = useCallback(async () => {
    if (!ownerId) return;
    await flush(ownerId, async (payload) => {
      // Read the token FRESH at send time (revision 4 / T3) — not a value
      // closed over at mount. A long offline stretch can leave a mount-time
      // token stale; re-reading here gives gotrue's autoRefreshToken a
      // chance to have already replaced it by the time we actually send,
      // and pairs with isRetryable's status===401 clause so a still-stale
      // token holds the envelope for the next trigger instead of dropping it.
      const { data } = await supabase.auth.getSession();
      await submitRequest(payload, data.session?.access_token ?? null);
    });
    refresh();
  }, [ownerId, refresh]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  useEffect(() => {
    void runFlush();

    const onOnline = () => void runFlush();
    const onVisibility = () => {
      if (document.visibilityState === "visible") void runFlush();
    };
    window.addEventListener("online", onOnline);
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      window.removeEventListener("online", onOnline);
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [runFlush]);

  // SIGNED_OUT notice: reads the owner id from the closure over the
  // PRE-signout render, NOT from the callback's own `session` argument —
  // by the time SIGNED_OUT fires that argument is null, so counting from it
  // would always find zero envelopes and the toast would never fire.
  useEffect(() => {
    const { data: sub } = supabase.auth.onAuthStateChange((event) => {
      if (event !== "SIGNED_OUT") return;
      const held = pendingFor(ownerId);
      if (held > 0) {
        toast(`${held} request${held === 1 ? "" : "s"} still waiting to send`, {
          description: "They'll send when you sign back in on this browser.",
        });
      }
    });
    return () => sub.subscription.unsubscribe();
  }, [ownerId]);

  return { pending, refresh };
}
